import { useRef, useState, type RefObject } from 'react'
import { useTranslation } from '../i18n/I18nProvider'
import { useTheme } from '../theme/ThemeProvider'
import { PrimaryButton, SecondaryButton } from './Button'

/**
 * The "Add to Home Screen" nudge (D30/EC-14, D160), ported from
 * `prototype/screens/shared.js` + `app.js`'s `flyNudgeToMenu()`. Cancel animates the card
 * toward the drawer's hamburger button rather than just disappearing; Add hands off to the
 * platform install flow (`useInstallPrompt`, wired by the caller) and then dismisses the
 * same way.
 *
 * **`menuButtonRef` is optional, and the card's home is now a screen that has no hamburger.**
 * It sits on the sign-in screen, where there is no app bar to fly into — so the flight is not a
 * fallback that happened to be missing a target, it is a thing that screen cannot say. With no
 * ref, or with the ref empty, Cancel dismisses plainly. What the flight used to teach ("it went
 * into the menu") is carried instead by the red dot the hamburger wears from the first signed-in
 * screen onward.
 *
 * This component holds no dismissed flag: the caller decides whether to show it, and (D160)
 * shows it again on every fresh arrival at this screen rather than once per device — see
 * `App.tsx`'s `nudgeDismissed` state for where that reset lives.
 */
export function NudgeSheet({
  visible,
  menuButtonRef,
  onAdd,
  onDismiss,
}: {
  readonly visible: boolean
  readonly menuButtonRef?: RefObject<HTMLButtonElement>
  onAdd(): void
  onDismiss(): void
}): JSX.Element | null {
  const { t } = useTranslation('nudge')
  const tokens = useTheme()
  const sheetRef = useRef<HTMLDivElement>(null)
  const [flying, setFlying] = useState(false)
  const [flyStyle, setFlyStyle] = useState<{ transform: string; opacity: string }>({
    transform: '',
    opacity: '',
  })

  if (!visible && !flying) return null

  function flyToMenu(): void {
    const sheet = sheetRef.current
    const menu = menuButtonRef?.current ?? null
    if (sheet === null || menu === null) {
      onDismiss()
      return
    }
    const sheetRect = sheet.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()
    const dx = menuRect.left + menuRect.width / 2 - (sheetRect.left + sheetRect.width / 2)
    const dy = menuRect.top + menuRect.height / 2 - (sheetRect.top + sheetRect.height / 2)
    setFlying(true)
    setFlyStyle({ transform: `translate(${dx}px, ${dy}px) scale(.05)`, opacity: '0' })
    window.setTimeout(() => {
      setFlying(false)
      setFlyStyle({ transform: '', opacity: '' })
      onDismiss()
    }, tokens.motion.nudgeFlight)
  }

  return (
    <div
      ref={sheetRef}
      className={`nudge-sheet ${visible ? 'show' : ''} ${flying ? 'flying' : ''}`.trim()}
      style={flying ? flyStyle : undefined}
    >
      <div className="nudge-card">
        <div className="nudge-title">{t('title')}</div>
        <div className="nudge-body">{t('body')}</div>
        <div className="nudge-actions">
          <SecondaryButton onClick={flyToMenu}>{t('notNow')}</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              onAdd()
              onDismiss()
            }}
          >
            {t('add')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
